import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { gsap } from 'gsap';

/**
 * Carrousel "éventail" (fan carousel) : les cartes s'étalent en éventail
 * autour d'une carte centrale mise en avant, avec déplacement fluide GSAP,
 * effet de poussée au survol et pagination (flèches + pastilles) quand il y a
 * plus de 7 cartes. Adapté de card-fan-carousel.tsx (shadcn) pour ce projet
 * (React/JS + styles inline), et étendu pour afficher les médias de la galerie
 * (photo / vidéo / live) comme sur la landing page.
 *
 * `items` : médias au format renvoyé par /activities/gallery/ :
 *   { id, media_type: 'photo'|'video'|'live', file_url, caption, activity_name, church_name }
 * `onItemSelect(index)` : appelé au clic sur une carte.
 */
export default function FanCarousel({ items, onItemSelect, style = {}, className = '' }) {
  const containerRef = useRef(null);
  const isAnimating = useRef(false);
  const hasEntered = useRef(false);
  const directionRef = useRef(null);
  const prevVisible = useRef(new Set());

  // Ne garder que les médias réellement affichables : une image/vidéo doit avoir
  // un fichier, un live doit avoir une URL ou un fichier. On évite ainsi les
  // cartes vides quand la galerie contient des entrées incomplètes.
  const cards = useMemo(() => (items || []).filter(m => {
    if (m.media_type === 'live') return !!(m.file_url || m.external_url);
    return !!m.file_url;
  }), [items]);
  const totalCards = cards.length;
  const needsPagination = totalCards > 7;
  const [centerIndex, setCenterIndex] = useState(needsPagination ? 3 : totalCards >> 1);
  const [failedIndexes, setFailedIndexes] = useState(new Set());

  // La galerie arrive en asynchrone : au montage on a 0 carte. Quand les cartes
  // chargent, on resynchronise une seule fois la carte centrale (sinon la carte
  // du milieu et la pastille active seraient fausses au premier affichage).
  const syncedInitial = useRef(false);
  useEffect(() => {
    if (syncedInitial.current || !totalCards) return;
    syncedInitial.current = true;
    setCenterIndex(needsPagination ? 3 : totalCards >> 1);
  }, [totalCards, needsPagination]);

  const getVisibleMap = useCallback((center) => {
    const map = new Map();
    if (!needsPagination) {
      cards.forEach((_, i) => map.set(i, i));
      return map;
    }
    for (let slot = 0; slot < 7; slot++) {
      map.set(((center + slot - 3) % totalCards + totalCards) % totalCards, slot);
    }
    return map;
  }, [totalCards, needsPagination, cards]);

  const cycle = useCallback((direction) => {
    if (isAnimating.current || !needsPagination) return;
    isAnimating.current = true;
    directionRef.current = direction;
    setCenterIndex(prev =>
      direction === 'right' ? (prev + 1) % totalCards : (prev - 1 + totalCards) % totalCards
    );
  }, [totalCards, needsPagination]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !totalCards) return;

    const cardElements = Array.from(container.querySelectorAll('.fan-card'));
    if (!cardElements.length) return;

    const visibleMap = getVisibleMap(centerIndex);
    const previouslyVisible = prevVisible.current;
    const direction = directionRef.current;
    const isFirstMount = !hasEntered.current;
    const multiplier = getResponsiveMultiplier(window.innerWidth);
    const hMult = getHeightMultiplier(window.innerWidth);
    const slotCount = needsPagination ? 7 : totalCards;
    const config = (slot) => getSlotConfig(slotCount, slot);

    if (isFirstMount) isAnimating.current = true;

    let completedCount = 0;
    const visibleCount = visibleMap.size;
    const onCardDone = () => {
      if (++completedCount >= visibleCount) {
        isAnimating.current = false;
        if (isFirstMount) hasEntered.current = true;
      }
    };

    cardElements.forEach((card, cardIndex) => {
      const slot = visibleMap.get(cardIndex);
      const wasVisible = previouslyVisible.has(cardIndex);

      if (slot !== undefined) {
        const { x, y, rot, scale, zIndex } = config(slot);
        const target = {
          x: `${x * multiplier}rem`,
          y: `${y * hMult}rem`,
          rotation: rot,
          scale,
          opacity: 1,
          zIndex,
        };

        if (isFirstMount) {
          gsap.set(card, { x: 0, y: `${12 * hMult}rem`, rotation: 0, scale: 0.5, opacity: 0 });
          gsap.to(card, { ...target, duration: 1.2, ease: 'elastic.out(1.05,.78)', delay: 0.2 + slot * 0.06, onComplete: onCardDone });
        } else if (!wasVisible) {
          const enterX = direction === 'right' ? 40 : -40;
          gsap.set(card, { x: `${enterX}rem`, y: `${y * hMult}rem`, rotation: direction === 'right' ? 30 : -30, scale: 0.5, opacity: 0 });
          gsap.to(card, { ...target, duration: 0.6, ease: 'power2.out', onComplete: onCardDone });
        } else {
          gsap.to(card, { ...target, duration: 0.5, ease: 'power2.out', onComplete: onCardDone });
        }
      } else if (wasVisible) {
        const exitX = direction === 'right' ? -40 : 40;
        gsap.to(card, { x: `${exitX}rem`, opacity: 0, scale: 0.5, rotation: direction === 'right' ? -30 : 30, duration: 0.4, ease: 'power2.in', zIndex: 0 });
      } else if (isFirstMount) {
        gsap.set(card, { opacity: 0, scale: 0.3, x: 0, y: 0, zIndex: 0 });
      }
    });

    prevVisible.current = new Set(visibleMap.keys());

    // Hover : poussée des cartes autour de celle survolée
    const visibleEntries = [];
    cardElements.forEach((el, i) => {
      const slot = visibleMap.get(i);
      if (slot !== undefined) visibleEntries.push({ el, slot });
    });
    visibleEntries.sort((a, b) => a.slot - b.slot);

    let activeSlot = null;
    let leaveTimer = null;
    const centerSlot = visibleEntries.length >> 1;

    const updateHoverLayout = (hoveredSlot) => {
      const mult = getResponsiveMultiplier(window.innerWidth);
      const hM = getHeightMultiplier(window.innerWidth);

      visibleEntries.forEach(({ el, slot }) => {
        const base = config(slot);
        let targetX = base.x * mult;
        let targetY = base.y * hM;
        let targetRot = base.rot;
        let targetScale = base.scale;
        let delay = 0;

        if (hoveredSlot !== null) {
          const distance = Math.abs(slot - hoveredSlot);
          delay = distance * 0.02;

          if (slot === hoveredSlot) {
            targetY -= 2.5 * hM;
            targetScale *= 1.08;
          } else {
            const normalized = centerSlot > 0 ? (slot - centerSlot) / centerSlot : 0;
            const pushStrength = 8 * (1 - Math.abs(normalized)) * (1 + 0.2 * Math.max(0, 3 - distance));

            if (slot < hoveredSlot) {
              targetX -= pushStrength * mult;
              targetRot -= 3 / (distance + 1);
            } else {
              targetX += pushStrength * mult;
              targetRot += 3 / (distance + 1);
            }

            if (slot === visibleEntries.length - 1 && hoveredSlot < centerSlot) targetY -= 1 * hM;
            if (slot === 0 && hoveredSlot > centerSlot) targetY -= 1 * hM;
          }
        } else {
          delay = Math.abs(slot - centerSlot) * 0.02;
        }

        gsap.to(el, {
          x: `${targetX}rem`, y: `${targetY}rem`, rotation: targetRot, scale: targetScale,
          duration: 0.5, delay, ease: 'elastic.out(1,.75)', overwrite: 'auto',
        });
        gsap.set(el, { zIndex: base.zIndex });
      });
    };

    const enterHandlers = visibleEntries.map(({ el, slot }) => {
      const handler = () => {
        if (isAnimating.current) return;
        if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
        if (activeSlot !== slot) { activeSlot = slot; updateHoverLayout(slot); }
      };
      el.addEventListener('mouseenter', handler);
      return { el, handler };
    });

    const onMouseLeave = () => {
      if (isAnimating.current) return;
      if (leaveTimer) clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => { activeSlot = null; updateHoverLayout(null); }, 50);
    };
    container.addEventListener('mouseleave', onMouseLeave);

    const onResize = () => { if (!isAnimating.current) updateHoverLayout(activeSlot); };
    window.addEventListener('resize', onResize);

    return () => {
      enterHandlers.forEach(({ el, handler }) => el.removeEventListener('mouseenter', handler));
      container.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('resize', onResize);
      if (leaveTimer) clearTimeout(leaveTimer);
      gsap.killTweensOf(cardElements);
    };
  }, [centerIndex, totalCards, getVisibleMap, needsPagination]);

  const markFailed = (i) => setFailedIndexes(prev => prev.has(i) ? prev : new Set(prev).add(i));

  // Fallback si une image/vidéo ne charge pas (fichier manquant ou cassé) :
  // on affiche un panneau neutre avec la légende plutôt qu'une carte vide.
  const renderMedia = (m, index) => {
    if (failedIndexes.has(index)) {
      return (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(145deg, #0f172a, #1e2a45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, boxSizing: 'border-box' }}>
          <span style={{ color: 'rgba(255,255,255,.85)', fontSize: 11, fontWeight: 700, textAlign: 'center', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {m.caption || m.activity_name || 'Média'}
          </span>
        </div>
      );
    }
    if (m.media_type === 'live') {
      return m.file_url
        ? <img src={m.file_url} alt={m.caption || 'Diffusion en direct'} loading="lazy" className="fan-media" onError={() => markFailed(index)} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          </div>;
    }
    if (m.media_type === 'video') {
      return <video src={m.file_url} className="fan-media" muted playsInline onError={() => markFailed(index)} />;
    }
    return <img src={m.file_url} alt={m.caption || m.activity_name || 'Média'} loading="lazy" className="fan-media" onError={() => markFailed(index)} />;
  };

  if (!totalCards) return null;

  const chevron = (direction) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points={direction === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
    </svg>
  );

  const arrowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 40, height: 40, borderRadius: '50%', border: '1.5px solid rgba(0,0,0,.1)',
    background: 'rgba(0,0,0,.05)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    color: 'rgba(0,0,0,.4)', cursor: 'pointer', flexShrink: 0, outline: 'none',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transition: 'all .3s',
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: '8px 0 4px', position: 'relative', zIndex: 20, ...style }} className={className}>
      <style>{`
        .fan-layout { position: relative; display: flex; align-items: center; justify-content: center; width: 100%; max-width: 80rem; height: 26rem; }
        @media (min-width: 480px) { .fan-layout { height: 31rem; } }
        @media (min-width: 640px) { .fan-layout { height: 34rem; } }
        @media (min-width: 768px) { .fan-layout { height: 41rem; } }
        @media (min-width: 1024px) { .fan-layout { height: 46rem; } }

        .fan-card {
          position: absolute; top: 50%; left: 50%;
          width: 7.7rem; height: 11.1rem; margin-left: -3.85rem; margin-top: -5.55rem;
          border-radius: 1rem; overflow: hidden; cursor: pointer;
          box-shadow: 0 18px 40px -12px rgba(15,23,42,.35);
          will-change: transform; backface-visibility: hidden; outline: none;
        }
        @media (min-width: 480px) { .fan-card { width: 9.1rem; height: 13rem; margin-left: -4.55rem; margin-top: -6.5rem; } }
        @media (min-width: 640px) { .fan-card { width: 10.5rem; height: 15rem; margin-left: -5.25rem; margin-top: -7.5rem; border-radius: 1.1rem; } }
        @media (min-width: 768px) { .fan-card { width: 13.3rem; height: 19rem; margin-left: -6.65rem; margin-top: -9.5rem; border-radius: 1.25rem; } }
        @media (min-width: 1024px) { .fan-card { width: 15.4rem; height: 22rem; margin-left: -7.7rem; margin-top: -11rem; border-radius: 1.4rem; } }

        .fan-media { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 10; }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '90rem' }}>
        <div ref={containerRef} className="fan-layout">
          {cards.map((m, index) => (
            <div
              key={m.id ?? index}
              className="fan-card"
              tabIndex={0}
              role="button"
              aria-label={m.caption || m.activity_name || (m.media_type === 'live' ? 'Diffusion en direct' : 'Média')}
              onClick={() => onItemSelect?.(index)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemSelect?.(index); } }}
            >
              {renderMedia(m, index)}
              {m.media_type === 'live' && (
                <span style={{ position: 'absolute', top: 8, left: 8, background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '3px 8px', zIndex: 20 }}>LIVE</span>
              )}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 45%, rgba(0,0,0,.78))', zIndex: 15 }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '9px 11px 10px', zIndex: 20 }}>
                <div style={{ color: '#fff', fontSize: 12.5, fontWeight: 700, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {m.caption || m.activity_name || (m.media_type === 'live' ? 'Diffusion en direct' : '')}
                </div>
                {m.church_name && (
                  <div style={{ color: 'rgba(255,255,255,.75)', fontSize: 11, fontWeight: 600, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.church_name}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {needsPagination && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 18, zIndex: 30 }}>
          <button className="fan-arrow" style={arrowStyle} onClick={() => cycle('left')} aria-label="Précédent">{chevron('left')}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {cards.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: i === centerIndex ? 'rgba(15,23,42,.75)' : 'rgba(15,23,42,.15)',
                  transform: i === centerIndex ? 'scale(1.3)' : 'none',
                  transition: 'all .3s',
                }}
              />
            ))}
          </div>
          <button className="fan-arrow" style={arrowStyle} onClick={() => cycle('right')} aria-label="Suivant">{chevron('right')}</button>
        </div>
      )}
    </section>
  );
}

/* ── helpers (portés tels quels depuis card-fan-carousel.tsx) ── */

const FAN_POSITIONS = [
  { rot: -21, scale: 0.7756, x: -30, y: 7.3, zIndex: 1 },
  { rot: -14, scale: 0.8498, x: -22, y: 4.0, zIndex: 2 },
  { rot: -7,  scale: 0.9346, x: -11, y: 1.3, zIndex: 3 },
  { rot: 0,   scale: 1.0,    x: 0,   y: 0.0, zIndex: 10 },
  { rot: 7,   scale: 0.9346, x: 11,  y: 1.3, zIndex: 3 },
  { rot: 14,  scale: 0.8498, x: 22,  y: 4.0, zIndex: 2 },
  { rot: 21,  scale: 0.7756, x: 30,  y: 7.3, zIndex: 1 },
];

function getSlotConfig(totalCards, slot) {
  if (totalCards >= 7) return FAN_POSITIONS[slot];
  const center = totalCards >> 1;
  const distance = totalCards > 1 ? (slot - center) / center : 0;
  const absDistance = Math.abs(distance);
  return {
    rot: distance * 21,
    scale: 1.0 - 0.2244 * absDistance * absDistance,
    x: distance * 30,
    y: absDistance * absDistance * 7.3,
    zIndex: 10 - Math.abs(slot - center),
  };
}

function getResponsiveMultiplier(width) {
  if (width < 480) return 0.28;
  if (width < 640) return 0.38;
  if (width < 768) return 0.5;
  if (width < 1024) return 0.75;
  return 1.0;
}

function getHeightMultiplier(width) {
  let idealPx;
  if (width < 480) idealPx = 22 * 16;       // 352px
  else if (width < 640) idealPx = 26 * 16;  // 416px
  else if (width < 768) idealPx = 28 * 16;  // 448px
  else if (width < 1024) idealPx = 34 * 16; // 544px
  else idealPx = 38 * 16;                    // 608px

  const available = window.innerHeight * 0.7; // 70vh budget
  if (available >= idealPx) return 1;
  return available / idealPx;
}
